/**
 * The cell of a decision — pickup date and rental duration — as a filter
 * of the Activity log (CU-869fhtp3a).
 *
 * Three shapes, one module:
 *
 * - **State** (`filters`): `pickup_from` / `pickup_to` as ISO strings or
 *   `''`, `duration_from` / `duration_to` as whole days or `null`.
 * - **URL**: the brief's keys — `pickup_from=…&pickup_to=…` and, for the
 *   duration, `duration=7` when the two ends are the same day count and
 *   `duration_from=7&duration_to=14` when they are not. This is what an
 *   operator copies and pastes; `duration=7` is what Thibault would type.
 * - **Wire**: the four names the webback accepts, `pickup_from`,
 *   `pickup_to`, `duration_from`, `duration_to`, each side optional. The
 *   server never sees `duration`: it is expanded here, once, the way
 *   `from` becomes `date_from` in `query()`.
 *
 * No React and no `date-fns`, so this runs under `node --test`; the date
 * formatter is injected like in `pickerState`.
 */
import { fromIso } from '../../@services/dateRange.js'

export const DURATION_MIN = 1
export const DURATION_MAX = 180

const CELL_KEYS = ['pickup_from', 'pickup_to', 'duration_from', 'duration_to']
const URL_KEYS = [...CELL_KEYS, 'duration']

/** The filter keys the counters follow — the window and the cell — as
 *  opposed to the ones that only narrow the list (office, ACRISS, type,
 *  facets, search). The "N of M shown" tail reads this (D6). */
export const windowKeys = ['from', 'to', 'pickup_from', 'pickup_to', 'duration_from', 'duration_to']

const EMPTY_CELL = () => ({ pickup_from: '', pickup_to: '', duration_from: null, duration_to: null })

/** A whole number of days inside the bounds, or `null`. */
function parseDays(s) {
	const t = String(s ?? '').trim()
	if (!/^\d+$/.test(t)) return null
	const n = Number(t)
	return n >= DURATION_MIN && n <= DURATION_MAX ? n : null
}

/** An ISO date the calendar could have produced, or `''`. */
function parseDay(s) {
	const t = String(s ?? '').trim()
	return fromIso(t) ? t : ''
}

/** Query string → the four keys of the cell. Bad values read as absent;
 *  `duration` wins over the pair; `from > to` is put in order. */
export function readCell(search) {
	const q = new URLSearchParams(search || '')
	const cell = EMPTY_CELL()
	let a = parseDay(q.get('pickup_from')), b = parseDay(q.get('pickup_to'))
	if (a && b && a > b) [a, b] = [b, a]
	cell.pickup_from = a
	cell.pickup_to = b

	const both = parseDays(q.get('duration'))
	let lo = both ?? parseDays(q.get('duration_from'))
	let hi = both ?? parseDays(q.get('duration_to'))
	if (lo != null && hi != null && lo > hi) [lo, hi] = [hi, lo]
	cell.duration_from = lo
	cell.duration_to = hi
	return cell
}

/** Just the four keys of a filters object — what the counters depend on. */
export function cellOf(filters) {
	const f = filters || {}
	return {
		pickup_from: f.pickup_from || '',
		pickup_to: f.pickup_to || '',
		duration_from: f.duration_from ?? null,
		duration_to: f.duration_to ?? null,
	}
}

/** Filters → the wire pairs, present sides only. The one translation. */
export function cellParams(filters) {
	const c = cellOf(filters)
	const out = {}
	if (c.pickup_from) out.pickup_from = c.pickup_from
	if (c.pickup_to) out.pickup_to = c.pickup_to
	if (c.duration_from != null) out.duration_from = String(c.duration_from)
	if (c.duration_to != null) out.duration_to = String(c.duration_to)
	return out
}

/**
 * The search string with the cell written into it: the five URL keys
 * removed and the active ones put back, every other key untouched and
 * in its order. Returns exactly what `location.search` would hold —
 * `?…` or `''` — so the caller can compare before navigating. An empty
 * side is absent, never `pickup_from=`.
 */
export function writeCell(search, filters) {
	const q = new URLSearchParams(search || '')
	for (const k of URL_KEYS) q.delete(k)
	// The Pass chip's ✕ empties `execution_id` in state but the URL kept
	// it, and a shared link brought the pass back (QA, 2026-10-15).
	if (filters?.execution_id === '') q.delete('execution_id')
	const c = cellOf(filters)
	if (c.pickup_from) q.set('pickup_from', c.pickup_from)
	if (c.pickup_to) q.set('pickup_to', c.pickup_to)
	if (c.duration_from != null && c.duration_from === c.duration_to) {
		q.set('duration', String(c.duration_from))
	} else {
		if (c.duration_from != null) q.set('duration_from', String(c.duration_from))
		if (c.duration_to != null) q.set('duration_to', String(c.duration_to))
	}
	const s = q.toString()
	return s ? `?${s}` : ''
}

const days = (n) => `${n} day${n === 1 ? '' : 's'}`

/** `"pickup 15 Nov 2026 · 3 days"`, only the active part, or `''`. */
export function cellSentence(filters, fmt) {
	const c = cellOf(filters)
	const parts = []
	const a = fromIso(c.pickup_from), b = fromIso(c.pickup_to)
	if (a && b) parts.push(c.pickup_from === c.pickup_to ? `pickup ${fmt(a)}` : `pickup ${fmt(a)} → ${fmt(b)}`)
	else if (a) parts.push(`pickup from ${fmt(a)}`)
	else if (b) parts.push(`pickup until ${fmt(b)}`)

	const lo = c.duration_from, hi = c.duration_to
	if (lo != null && hi != null) parts.push(lo === hi ? days(lo) : `${lo}–${hi} days`)
	else if (lo != null) parts.push(`from ${days(lo)}`)
	else if (hi != null) parts.push(`up to ${days(hi)}`)
	return parts.join(' · ')
}

/** `"31 Dec · 7 d"` for the row, `''` when either coordinate is missing
 *  — a setup change has no scope (D8). */
export function cellRowLabel(scope, fmt) {
	const d = fromIso(scope?.pickup_date)
	const n = scope?.duration_days
	if (!d || n == null || n === '') return ''
	return `${fmt(d)} · ${n} d`
}
